import { z } from "zod";

export const QA_PAYER_PAYMENT_RUN_RECIPE_ID =
  "int956_hosted_payer_scope_payments_v1" as const;

export const QaPayerPaymentRunStageSchema = z.enum([
  "pilot",
  "payer_modes",
  "scale_10",
]);
export type QaPayerPaymentRunStage = z.infer<
  typeof QaPayerPaymentRunStageSchema
>;

export const QaPayerPaymentRunScenarioSchema = z.enum([
  "delegated_assignment",
  "primary_nondelegated",
  "primary_full_family",
  "scale_payment",
]);
export type QaPayerPaymentRunScenario = z.infer<
  typeof QaPayerPaymentRunScenarioSchema
>;

export const QaPayerPaymentRunStatusSchema = z.enum([
  "previewed",
  "provisioned",
  "executing",
  "cleaned",
  "recovery_required",
]);
export type QaPayerPaymentRunStatus = z.infer<
  typeof QaPayerPaymentRunStatusSchema
>;

export const QA_PAYER_PAYMENT_RUN_LIMITS = {
  pilot: {
    familyCount: 1,
    maxIntentCount: 1,
    maxIntentCents: 100,
    maxAggregateCents: 100,
    maxConcurrency: 1,
  },
  payer_modes: {
    familyCount: 3,
    maxIntentCount: 3,
    maxIntentCents: 100,
    maxAggregateCents: 300,
    maxConcurrency: 1,
  },
  scale_10: {
    familyCount: 10,
    maxIntentCount: 10,
    maxIntentCents: 100,
    maxAggregateCents: 1_000,
    maxConcurrency: 2,
  },
} as const satisfies Record<
  QaPayerPaymentRunStage,
  {
    familyCount: number;
    maxIntentCount: number;
    maxIntentCents: number;
    maxAggregateCents: number;
    maxConcurrency: number;
  }
>;

const QA_PAYER_PAYMENT_RUN_SCENARIOS = {
  pilot: ["delegated_assignment"],
  payer_modes: [
    "delegated_assignment",
    "primary_nondelegated",
    "primary_full_family",
  ],
  scale_10: Array.from({ length: 10 }, () => "scale_payment"),
} as const satisfies Record<
  QaPayerPaymentRunStage,
  readonly QaPayerPaymentRunScenario[]
>;

export const resolveQaPayerPaymentRunScenarios = (
  stage: QaPayerPaymentRunStage,
): readonly QaPayerPaymentRunScenario[] => [
  ...QA_PAYER_PAYMENT_RUN_SCENARIOS[stage],
];

const Sha256Schema = z.string().regex(/^[a-f0-9]{64}$/i);
const DeployedShaSchema = z.string().regex(/^[a-f0-9]{40}$/i);
const RunIdSchema = z.string().regex(/^INT-956-QA-\d{8}T\d{6}Z-[a-z0-9]{8}$/);

export const QaPayerPaymentRunPromotionApprovalSchema = z
  .object({
    predecessorRunId: RunIdSchema,
    predecessorRunDigest: Sha256Schema,
    reviewedBy: z.string().min(3).max(200),
    reviewedAt: z.string().datetime(),
    evidenceId: z.string().min(1).max(200),
  })
  .strict();
export type QaPayerPaymentRunPromotionApproval = z.infer<
  typeof QaPayerPaymentRunPromotionApprovalSchema
>;

export const QaPayerPaymentRunLimitsSchema = z
  .object({
    familyCount: z.number().int().min(1).max(10),
    maxIntentCount: z.number().int().min(1).max(10),
    maxIntentCents: z.number().int().positive().max(100),
    maxAggregateCents: z.number().int().positive().max(1_000),
    maxConcurrency: z.number().int().min(1).max(2),
  })
  .strict();

export const QaPayerPaymentRunFixturePreviewSchema = z
  .object({
    slot: z.string().regex(/^[a-j]$/),
    scenario: QaPayerPaymentRunScenarioSchema,
    safeLabel: z.string().min(1).max(120),
    applicantId: z.string().uuid(),
    familyIdHash: Sha256Schema,
    openingBalanceCents: z.literal(100),
  })
  .strict();

export const QaPayerPaymentRunPreviewInputSchema = z
  .object({
    schoolId: z.string().min(1),
    tenantSlug: z.string().min(1),
    stage: QaPayerPaymentRunStageSchema,
    releaseEvidenceId: z.string().min(1).max(200),
    externallyVerifiedDeployedSha: DeployedShaSchema,
    cleanupOwner: z.string().min(3).max(200),
    promotionApproval: QaPayerPaymentRunPromotionApprovalSchema.optional(),
  })
  .strict();

export const QaPayerPaymentRunPreviewSchema = z
  .object({
    recipeId: z.literal(QA_PAYER_PAYMENT_RUN_RECIPE_ID),
    runId: RunIdSchema,
    stage: QaPayerPaymentRunStageSchema,
    status: z.literal("previewed"),
    environment: z.literal("dev"),
    orgId: z.string().uuid(),
    schoolId: z.string().min(1),
    tenantSlug: z.string().min(1),
    academicYear: z.string().min(1),
    releaseEvidenceId: z.string().min(1).max(200),
    externallyVerifiedDeployedSha: DeployedShaSchema,
    cleanupOwner: z.string().min(3).max(200),
    promotionApproval: QaPayerPaymentRunPromotionApprovalSchema.optional(),
    limits: QaPayerPaymentRunLimitsSchema,
    fixtures: z.array(QaPayerPaymentRunFixturePreviewSchema).min(1).max(10),
    allowlistDigest: Sha256Schema,
    beforeStateDigest: Sha256Schema,
    confirmationText: z.string().min(1).max(200),
    canApply: z.boolean(),
    releaseBinding: z.literal("externally_verified_release_preflight"),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      (value.stage === "pilot" && value.promotionApproval) ||
      (value.stage !== "pilot" && !value.promotionApproval)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["promotionApproval"],
        message:
          "INT-956 promoted stages require predecessor review approval and pilot forbids it.",
      });
    }
    const expectedLimits = QA_PAYER_PAYMENT_RUN_LIMITS[value.stage];
    if (JSON.stringify(value.limits) !== JSON.stringify(expectedLimits)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["limits"],
        message: `INT-956 ${value.stage} limits do not match the fixed recipe.`,
      });
    }
    const expectedScenarios = resolveQaPayerPaymentRunScenarios(value.stage);
    if (value.fixtures.length !== expectedLimits.familyCount) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["fixtures"],
        message: `INT-956 ${value.stage} fixture count must be ${expectedLimits.familyCount}.`,
      });
      return;
    }
    value.fixtures.forEach((fixture, index) => {
      if (
        fixture.slot !== String.fromCharCode(97 + index) ||
        fixture.scenario !== expectedScenarios[index]
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["fixtures", index],
          message: `INT-956 ${value.stage} fixture sequence does not match the fixed recipe.`,
        });
      }
    });
  });

export const QaPayerPaymentRunApplyInputSchema =
  QaPayerPaymentRunPreviewInputSchema.extend({
    runId: RunIdSchema,
    expectedBeforeStateDigest: Sha256Schema,
    confirmationText: z.string().min(1).max(200),
  }).strict();

export const QaPayerPaymentRunProvisionedFixtureSchema =
  QaPayerPaymentRunFixturePreviewSchema.extend({
    familyId: z.string().uuid(),
    ledgerEntryId: z.string().uuid(),
    isolationDigest: Sha256Schema,
  }).strict();

export const QaPayerPaymentRunProviderMutationSchema = z
  .object({
    mutationId: Sha256Schema,
    operation: z.enum(["create", "confirm"]),
    paymentFingerprint: z.string().min(1).max(255),
    providerIdempotencyKey: z.string().min(1).max(255),
    providerIntentId: z
      .string()
      .regex(/^pi_[A-Za-z0-9_]{1,252}$/)
      .optional(),
    status: z.enum(["active", "resolved", "indeterminate"]),
    outcomeDigest: Sha256Schema.optional(),
    occurredAt: z.string().datetime(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.operation === "confirm" && !value.providerIntentId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["providerIntentId"],
        message: "INT-956 provider confirmation requires an exact provider intent.",
      });
    }
    if (
      value.status === "active" &&
      (value.outcomeDigest ||
        (value.operation === "create" && value.providerIntentId))
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["status"],
        message: "INT-956 active provider mutations cannot contain an outcome.",
      });
    }
    if (
      value.status !== "active" &&
      (!value.outcomeDigest ||
        (value.status === "resolved" && !value.providerIntentId))
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["outcomeDigest"],
        message: "INT-956 settled provider mutations require a durable provider outcome.",
      });
    }
  });

export const QaPayerPaymentRunIntentClaimSchema = z
  .object({
    paymentFingerprint: z.string().min(1).max(255),
    slot: z.string().regex(/^[a-j]$/),
    familyId: z.string().uuid(),
    scenario: QaPayerPaymentRunScenarioSchema,
    payerMode: z.enum([
      "delegated_assignment",
      "primary_nondelegated",
      "primary_full_family",
    ]),
    paymentSelectionMode: z.enum(["full-balance", "line-items"]),
    selectedLineItemIndexes: z.array(z.number().int().nonnegative()).max(10),
    amountCents: z.number().int().positive().max(100),
    payerPartyId: z.string().min(1).max(200),
    payerAssignmentId: z.string().min(1).max(200),
    payerAssignmentVersion: z.number().int().positive(),
    status: z.enum(["reserved", "completed"]),
    providerIntentId: z.string().min(1).max(255).optional(),
    providerMutations: z
      .array(QaPayerPaymentRunProviderMutationSchema)
      .max(2)
      .optional(),
    occurredAt: z.string().datetime(),
  })
  .strict();

export const QaPayerPaymentRunCleanupEvidenceSchema = z
  .object({
    familyId: z.string().uuid(),
    providerIntentId: z.string().min(1).max(255),
    idempotencyKey: z.string().min(1).max(255),
    webhookEventId: z.string().min(1).max(255),
    ledgerPaymentId: z.string().uuid(),
    payerAssignmentId: z.string().min(1).max(200),
    compensationLedgerEntryId: z.string().uuid(),
  })
  .strict();
export type QaPayerPaymentRunCleanupEvidence = z.infer<
  typeof QaPayerPaymentRunCleanupEvidenceSchema
>;

export const QaPayerPaymentRunReceiptHashesSchema = z
  .object({
    paymentIntents: z.array(Sha256Schema).max(10),
    webhooks: z.array(Sha256Schema).max(10),
    ledgerPayments: z.array(Sha256Schema).max(10),
    assignments: z.array(Sha256Schema).max(20),
    compensations: z.array(Sha256Schema).max(10),
    balances: z.array(Sha256Schema).max(10),
  })
  .strict();

export const QaPayerPaymentRunReceiptSchema = z
  .object({
    recipeId: z.literal(QA_PAYER_PAYMENT_RUN_RECIPE_ID),
    runId: RunIdSchema,
    stage: QaPayerPaymentRunStageSchema,
    status: z.enum([
      "provisioned",
      "executing",
      "cleaned",
      "recovery_required",
    ]),
    environment: z.literal("dev"),
    orgId: z.string().uuid(),
    schoolId: z.string().min(1),
    tenantSlug: z.string().min(1),
    academicYear: z.string().min(1),
    releaseEvidenceId: z.string().min(1).max(200),
    externallyVerifiedDeployedSha: DeployedShaSchema,
    cleanupOwner: z.string().min(3).max(200),
    promotionApproval: QaPayerPaymentRunPromotionApprovalSchema.optional(),
    limits: QaPayerPaymentRunLimitsSchema,
    fixtures: z.array(QaPayerPaymentRunProvisionedFixtureSchema).min(1).max(10),
    allowlistDigest: Sha256Schema,
    previewDigest: Sha256Schema,
    runDigest: Sha256Schema,
    intentClaims: z.array(QaPayerPaymentRunIntentClaimSchema).max(10).optional(),
    receiptHashes: QaPayerPaymentRunReceiptHashesSchema.optional(),
    occurredAt: z.string().datetime(),
    releaseBinding: z.literal("externally_verified_release_preflight"),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      (value.stage === "pilot" && value.promotionApproval) ||
      (value.stage !== "pilot" && !value.promotionApproval)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["promotionApproval"],
        message:
          "INT-956 promoted run receipt requires predecessor review approval.",
      });
    }
    const limits = QA_PAYER_PAYMENT_RUN_LIMITS[value.stage];
    if (value.fixtures.length !== limits.familyCount) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["fixtures"],
        message: `INT-956 ${value.stage} receipt fixture count must be ${limits.familyCount}.`,
      });
    }
    if (value.status === "cleaned" && !value.receiptHashes) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["receiptHashes"],
        message: "INT-956 cleaned run requires cleanup receipts.",
      });
    }
  });

export const QaPayerPaymentRunTransitionInputSchema = z
  .object({
    orgId: z.string().uuid(),
    runId: RunIdSchema,
    actorId: z.string().min(1).max(200),
    expectedRunDigest: Sha256Schema,
    toStatus: z.enum(["executing", "cleaned", "recovery_required"]),
    receiptHashes: QaPayerPaymentRunReceiptHashesSchema.optional(),
    cleanupEvidence: z.array(QaPayerPaymentRunCleanupEvidenceSchema).max(10).optional(),
  })
  .strict();

const FORWARD_TRANSITIONS: Record<
  QaPayerPaymentRunStatus,
  readonly QaPayerPaymentRunStatus[]
> = {
  previewed: ["provisioned"],
  provisioned: ["executing", "recovery_required"],
  executing: ["cleaned", "recovery_required"],
  cleaned: [],
  recovery_required: ["cleaned"],
};

export const assertQaPayerPaymentRunTransition = (
  from: QaPayerPaymentRunStatus,
  to: QaPayerPaymentRunStatus,
): QaPayerPaymentRunStatus => {
  if (!FORWARD_TRANSITIONS[from].includes(to)) {
    throw new Error(
      `INT-956 QA payment run transition ${from} -> ${to} is not allowed.`,
    );
  }
  return to;
};

export type QaPayerPaymentRunPreview = z.infer<
  typeof QaPayerPaymentRunPreviewSchema
>;
export type QaPayerPaymentRunReceipt = z.infer<
  typeof QaPayerPaymentRunReceiptSchema
>;
export type QaPayerPaymentRunProviderMutation = z.infer<
  typeof QaPayerPaymentRunProviderMutationSchema
>;
export type QaPayerPaymentRunReceiptHashes = z.infer<
  typeof QaPayerPaymentRunReceiptHashesSchema
>;
